import React, { useContext, useEffect, useState } from 'react';
import { IconButton, Popover } from '@mui/material';
import { ArrowDropDown, ChevronLeft, ChevronRight } from '@mui/icons-material';
import type { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
	MonthYearButton,
	MonthYearHeaderRoot,
	MonthYearPanel,
	MonthYearRow,
	MonthYearValue,
} from './styles';

/** `YYYY-MM` the parent wants on screen. The header moves the grid when it changes. */
export const CalendarMonthContext = React.createContext<string | undefined>(undefined);

const capitalize = (value: string): string =>
	value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const MonthYearHeader: React.FC<PickersCalendarHeaderProps> = ({
	currentMonth,
	onMonthChange,
	labelId,
}) => {
	const requestedMonth = useContext(CalendarMonthContext);
	const current = currentMonth as Dayjs;
	const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
	const year = current.year();
	const monthIndex = current.month();

	useEffect(() => {
		if (!requestedMonth) return;
		const [reqYear, reqMonth] = requestedMonth.split('-').map(Number);
		const reqIndex = (reqMonth ?? 1) - 1;
		if (year === reqYear && monthIndex === reqIndex) return;
		onMonthChange(dayjs(new Date(reqYear, reqIndex, 1)));
	}, [requestedMonth, year, monthIndex, onMonthChange]);

	const shift = (unit: 'month' | 'year', delta: number) => {
		onMonthChange(current.add(delta, unit));
	};

	const monthName = capitalize(current.locale('es').format('MMMM'));

	return (
		<MonthYearHeaderRoot>
			<MonthYearButton
				id={labelId}
				onClick={(event) => setAnchor(event.currentTarget)}
				aria-haspopup='dialog'
				aria-expanded={anchor !== null}
				endIcon={<ArrowDropDown />}
			>
				{monthName} {year}
			</MonthYearButton>
			<Popover
				open={anchor !== null}
				anchorEl={anchor}
				onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<MonthYearPanel>
					<MonthYearRow>
						<IconButton aria-label='Mes anterior' onClick={() => shift('month', -1)}>
							<ChevronLeft />
						</IconButton>
						<MonthYearValue>{monthName}</MonthYearValue>
						<IconButton aria-label='Mes siguiente' onClick={() => shift('month', 1)}>
							<ChevronRight />
						</IconButton>
					</MonthYearRow>
					<MonthYearRow>
						<IconButton aria-label='Año anterior' onClick={() => shift('year', -1)}>
							<ChevronLeft />
						</IconButton>
						<MonthYearValue>{year}</MonthYearValue>
						<IconButton aria-label='Año siguiente' onClick={() => shift('year', 1)}>
							<ChevronRight />
						</IconButton>
					</MonthYearRow>
				</MonthYearPanel>
			</Popover>
		</MonthYearHeaderRoot>
	);
};

export default MonthYearHeader;
